/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { CheckCircle, AlertTriangle } from 'lucide-react';

const C = {
  cream:'#F7F6EF', charcoal:'#2F3A42', navy:'#445366', mid:'#7A8A96',
  border:'#D8DEE3', skyBlue:'#BCD9E5', green:'#7EA594', greenLight:'#EAF2EF',
  red:'#DC3545', redLight:'#FFF0F0', amber:'#E07B00', amberLight:'#FFF3E0',
  white:'#FFFFFF',
};

const TYPE_LABELS = {
  verbal:  { label: 'Verbal Warning',      color: C.amber, bg: C.amberLight },
  written: { label: 'Written Warning',     color: '#E4AC49', bg: '#FDF3DC'  },
  final:   { label: 'Final Warning',       color: C.red,   bg: C.redLight  },
  pip:     { label: 'Performance Improvement Plan', color: '#3A8FA3', bg: '#EAF4F8' },
};

export default function Warning() {
  const token = window.location.pathname.split('/warning/')[1]?.split('/')[0];

  const [warning,    setWarning]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [confirmed,  setConfirmed]  = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done,       setDone]       = useState(false);
  const [ticked,     setTicked]     = useState(false);

  useEffect(() => {
    if (!token) { setError('Invalid link.'); setLoading(false); return; }
    load();
  }, [token]);

  async function load() {
    const { data, error: err } = await supabase
      .from('warnings')
      .select('*, team_members(name, venue)')
      .eq('token', token)
      .single();

    if (err || !data) { setError('This link is invalid or has expired.'); setLoading(false); return; }
    if (data.confirmed_at) setDone(true);
    setWarning(data);
    setLoading(false);
  }

  async function handleConfirm() {
    if (!ticked) return;
    setSubmitting(true);
    const { error: err } = await supabase
      .from('warnings')
      .update({ confirmed_at: new Date().toISOString() })
      .eq('token', token);
    if (err) { setSubmitting(false); return; }
    setDone(true);
    setWarning(p => ({ ...p, confirmed_at: new Date().toISOString() }));
    setSubmitting(false);
  }

  const typeConf  = warning ? (TYPE_LABELS[warning.type] || { label: warning.type, color: C.navy, bg: C.cream }) : null;
  const memberName = warning?.team_members?.name || '';
  const venue      = warning?.team_members?.venue || '';

  if (loading) return (
    <div style={{ minHeight:'100vh', background:C.cream, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:C.mid, fontSize:'14px' }}>Loading…</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight:'100vh', background:C.cream, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}>
      <div style={{ background:C.white, borderRadius:'16px', border:`1px solid ${C.border}`, padding:'32px', maxWidth:'420px', width:'100%', textAlign:'center' }}>
        <AlertTriangle size={32} color={C.amber} style={{ marginBottom:'12px' }}/>
        <div style={{ fontWeight:700, fontSize:'15px', color:C.charcoal, marginBottom:'8px' }}>Something went wrong</div>
        <div style={{ fontSize:'13px', color:C.mid }}>{error}</div>
      </div>
    </div>
  );

  if (done && !warning) return (
    <div style={{ minHeight:'100vh', background:C.cream, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}>
      <div style={{ background:C.white, borderRadius:'16px', border:`1px solid ${C.border}`, padding:'40px 32px', maxWidth:'440px', width:'100%', textAlign:'center', boxShadow:'0 4px 24px rgba(0,0,0,0.07)' }}>
        <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:C.greenLight, border:`2px solid ${C.green}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
          <CheckCircle size={28} color={C.green}/>
        </div>
        <div style={{ fontWeight:700, fontSize:'18px', color:C.charcoal, marginBottom:'8px' }}>Receipt confirmed</div>
        <div style={{ fontSize:'13px', color:C.mid, lineHeight:1.6 }}>
          Your confirmation has been recorded and timestamped. This has been marked on your employee file as <strong style={{ color:C.charcoal }}>TM has confirmed receipt</strong>.
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:C.cream }}>
      {/* Header */}
      <div style={{ background:C.navy, padding:'18px 24px' }}>
        <div style={{ maxWidth:'640px', margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'16px', color:C.white }}>Box Sociaal</div>
            <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px' }}>HR Document</div>
          </div>
          {typeConf && (
            <span style={{ padding:'4px 12px', borderRadius:'20px', fontSize:'12px', fontWeight:700, background:typeConf.bg, color:typeConf.color }}>
              {typeConf.label}
            </span>
          )}
        </div>
      </div>

      <div style={{ maxWidth:'640px', margin:'0 auto', padding:'24px 16px 48px' }}>

        {/* Identity block */}
        <div style={{ background:C.white, borderRadius:'14px', border:`1px solid ${C.border}`, padding:'20px 24px', marginBottom:'20px', boxShadow:'0 2px 12px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize:'12px', color:C.mid, marginBottom:'4px' }}>Issued to</div>
          <div style={{ fontWeight:700, fontSize:'20px', color:C.charcoal, marginBottom:'4px' }}>{memberName}</div>
          <div style={{ fontSize:'13px', color:C.mid }}>{venue}</div>
          <div style={{ marginTop:'12px', display:'flex', gap:'16px', fontSize:'12px', color:C.mid, flexWrap:'wrap' }}>
            <span>📅 {warning?.date ? new Date(warning.date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : '—'}</span>
            <span>👤 Issued by {warning?.issued_by}</span>
          </div>
        </div>

        {/* Warning content */}
        <div style={{ background:C.white, borderRadius:'14px', border:`1px solid ${typeConf?.color}44`, padding:'22px 24px', marginBottom:'20px', borderLeft:`4px solid ${typeConf?.color}` }}>

          {warning?.incident_date && (
            <div style={{ marginBottom:'16px', padding:'8px 12px', background:C.cream, borderRadius:'8px', fontSize:'12px', color:C.mid }}>
              Incident date: {new Date(warning.incident_date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}
            </div>
          )}

          <div style={{ marginBottom:'16px' }}>
            <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'8px' }}>
              {warning?.type === 'pip' ? 'Performance issues to address' : 'Description'}
            </div>
            <div style={{ fontSize:'14px', color:C.charcoal, lineHeight:1.7, whiteSpace:'pre-wrap' }}>{warning?.description}</div>
          </div>

          {warning?.expectation && (
            <div style={{ marginBottom:'16px', padding:'14px 16px', background:C.amberLight, borderRadius:'10px', border:`1px solid ${C.amber}44` }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:C.amber, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'8px' }}>
                {warning?.type === 'pip' ? 'Goals & expected improvements' : 'Expected improvement'}
              </div>
              <div style={{ fontSize:'14px', color:C.charcoal, lineHeight:1.7, whiteSpace:'pre-wrap' }}>{warning.expectation}</div>
            </div>
          )}

          {warning?.type === 'pip' && warning?.improvement_period_days && (
            <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
              <div style={{ padding:'10px 14px', background:C.cream, borderRadius:'8px', border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'3px' }}>Improvement period</div>
                <div style={{ fontSize:'14px', fontWeight:700, color:C.charcoal }}>{warning.improvement_period_days} days</div>
              </div>
              {warning.checkin_date && (
                <div style={{ padding:'10px 14px', background:C.cream, borderRadius:'8px', border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'3px' }}>Check-in date</div>
                  <div style={{ fontSize:'14px', fontWeight:700, color:C.charcoal }}>
                    {new Date(warning.checkin_date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}
                  </div>
                </div>
              )}
            </div>
          )}

          {warning?.support_offered && (
            <div style={{ marginTop:'16px', padding:'12px 14px', background:C.greenLight, borderRadius:'8px', border:`1px solid ${C.green}44` }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:C.green, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' }}>Support offered</div>
              <div style={{ fontSize:'13px', color:C.charcoal, lineHeight:1.6 }}>{warning.support_offered}</div>
            </div>
          )}
        </div>

        {/* Acknowledgement */}
        {done ? (
          <div style={{ background:C.greenLight, borderRadius:'14px', border:`2px solid ${C.green}`, padding:'24px', display:'flex', alignItems:'center', gap:'16px' }}>
            <div style={{ width:'44px', height:'44px', borderRadius:'50%', background:C.white, border:`2px solid ${C.green}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <CheckCircle size={24} color={C.green}/>
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:'14px', color:C.green, marginBottom:'4px' }}>Receipt confirmed</div>
              <div style={{ fontSize:'13px', color:C.green, lineHeight:1.5 }}>
                You confirmed receipt of this document on <strong>{new Date(warning?.confirmed_at).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}</strong>. This has been recorded on your employee file.
              </div>
            </div>
          </div>
        ) : (
          <div style={{ background:C.white, borderRadius:'14px', border:`2px solid ${ticked ? C.green : C.border}`, padding:'24px', transition:'border-color 0.2s' }}>
            <div style={{ fontWeight:600, fontSize:'14px', color:C.charcoal, marginBottom:'16px' }}>Acknowledgement</div>
            <div style={{ fontSize:'13px', color:C.mid, lineHeight:1.6, marginBottom:'20px' }}>
              By confirming below you acknowledge that you have received and read this {typeConf?.label?.toLowerCase() || 'document'}. Acknowledging receipt does not necessarily mean you agree with its contents.
            </div>

            <label style={{ display:'flex', alignItems:'flex-start', gap:'12px', cursor:'pointer', marginBottom:'20px' }}>
              <div onClick={() => setTicked(t => !t)} style={{ marginTop:'2px', flexShrink:0, cursor:'pointer' }}>
                {ticked
                  ? <CheckCircle size={22} color={C.green}/>
                  : <div style={{ width:22, height:22, borderRadius:'50%', border:`2px solid ${C.border}`, display:'inline-block' }}/>}
              </div>
              <span style={{ fontSize:'13px', color:C.charcoal, lineHeight:1.6 }}>
                I, <strong>{memberName}</strong>, confirm that I have received and read this {typeConf?.label?.toLowerCase() || 'document'} issued by {warning?.issued_by} on {warning?.date ? new Date(warning.date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : '—'}.
              </span>
            </label>

            <button onClick={handleConfirm} disabled={!ticked || submitting}
              style={{ width:'100%', padding:'13px', background:ticked ? C.navy : C.border, color:ticked ? C.white : C.mid, border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:600, cursor:ticked ? 'pointer' : 'not-allowed', transition:'all 0.2s' }}>
              {submitting ? 'Submitting…' : 'Confirm Receipt'}
            </button>

            {!ticked && (
              <div style={{ marginTop:'10px', fontSize:'12px', color:C.mid, textAlign:'center' }}>
                Tick the box above to confirm
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}